import type { TranslationShape } from '../types';

export const es = {
  common: {
    loading: 'Cargando...',
    initializing: 'Inicializando...',
    continue: 'Continuar',
    back: 'Atrás',
    retry: 'Reintentar',
    tryAgain: 'Intentar de nuevo',
    goBack: 'Volver',
    cancel: 'Cancelar',
    close: 'Cerrar',
    closeModal: 'Cerrar modal',
    closeWindow: 'Cerrar ventana',
    startOver: 'Comenzar de nuevo',
    switchToMobile: 'Cambiar a móvil',
    enableCamera: 'Habilitar cámara',
    returnToSite: 'Volver al sitio',
    notice: 'Aviso',
    done: 'Listo',
    saving: 'Guardando...',
    processing: 'Procesando...',
    error: 'Error',
    moreInfo: 'Más información',
    productFallback: 'verificación',
  },
  ageEstimateUnavailable: {
    title: 'No pudimos comprobar tu edad en este momento',
    description:
      'Algo falló por nuestra parte al revisar tu selfie. Puedes intentarlo de nuevo o verificarte con tu ID usando el selfie que acabas de tomar.',
    descriptionIdOnly:
      'Algo falló por nuestra parte al revisar tu selfie. Puedes continuar verificándote con tu ID usando el selfie que acabas de tomar.',
    useId: 'Verificar con ID',
  },
  pipelineRecovery: {
    title: 'La verificación se interrumpió temporalmente',
    description:
      'No pudimos continuar este paso ahora. Tu verificación sigue activa.',
    retrying: 'Lo estamos intentando de nuevo. Mantén esta página abierta.',
    readyToRetry: 'Inténtalo de nuevo cuando quieras.',
    retryingAction: 'Intentándolo de nuevo…',
  },
  lifecycleRecovery: {
    loadTitle: 'No pudimos cargar el siguiente paso', loadDescription: 'Comprueba tu conexión e inténtalo de nuevo.', retryTitle: 'Intentémoslo de nuevo',
    retryDescription: 'Algo interrumpió este paso. Inténtalo de nuevo.', retrySelfieTitle: 'Volvamos a tomar tu selfie', retrySelfieDescription: 'Mira a la cámara con buena luz e inténtalo de nuevo.', retryIdTitle: 'Tomemos otra foto de tu documento', retryIdDescription: 'Asegúrate de que todo tu documento se vea y se pueda leer bien.', restartStepTitle: 'Intentemos este paso de nuevo',
    restartStepDescription: 'Necesitamos iniciar este paso de nuevo para continuar.', startAgain: 'Empezar de nuevo', beginAgain: 'Volver a empezar',
    cannotContinueTitle: 'Esta verificación no puede continuar', cannotContinueDescription: 'Inicia una nueva verificación.', waitingTitle: 'Estamos terminando este paso…',
    waitingDescription: 'Mantén esta página abierta. Continuaremos automáticamente.', cancelledTitle: 'Verificación cancelada', completeTitle: 'Verificación completada',
    closeDescription: 'Puedes cerrar esta ventana.', returnDescription: 'Puedes volver al sitio.', continueTitle: 'Todo listo para continuar', continueDescription: 'Continúa con el siguiente paso.',
    rateLimitedTitle: 'Necesitamos un momento', rateLimitedDescription: 'Ahora no podemos consultar el siguiente paso. Tu verificación sigue activa.', checkStatus: 'Comprobar estado',
    finalizingTitle: 'Finalizando tu verificación…',
    finalizingDescription: 'Mantén esta página abierta. Normalmente tarda unos segundos.',
    unconfirmedTitle: 'Todavía estamos confirmando tu resultado',
    unconfirmedDescription: 'Está tardando más de lo habitual. No tendrás que repetir ningún paso.',
    checkAgain: 'Comprobar de nuevo',
  },
  footer: {
    terms: 'Términos',
    privacy: 'Privacidad',
    biometric: 'Biometría',
    languageLabel: 'Idioma',
    copyright: '© {{year}} {{companyName}}. Todos los derechos reservados.',
  },
  mainScreen: {
    title: 'Verifícate de forma anónima',
    description:
      'Verificación de edad rápida y privada. Nunca recopilamos ni compartimos tus datos.',
    iconAlt: 'Verificación segura',
    primaryAction: 'Verificar ahora',
    secondaryAction: 'Iniciar sesión con Emblem',
    separatorText: '¿Ya estás verificado?',
    missingSession:
      'Falta la configuración de la sesión. Inicia la verificación desde el principio.',
    directAccessBlocked:
      'El acceso directo no está permitido. Inicia la verificación desde el principio.',
    emblemResumeNotice:
      "Emblem sign-in wasn't completed. You can continue with verification here.",
    emblemNoReusableNotice:
      "You don't have a reusable Emblem pass for this site yet. You can continue with verification here.",
  },
  emblemStepUp: {
    title: 'One more verification step',
    description:
      'Your Emblem passkey worked, but this site requires a higher level of verification.',
    primaryAction: 'Continue verification',
    iconAlt: 'Emblem verification step up',
    detailTitle: 'What happens next',
    detailPoint1: 'Complete a quick SafePassage verification for this site.',
    detailPoint2:
      'After you finish, your Emblem passkey will be upgraded for future L2 sites.',
    privacyHint: 'You are still verifying privately. Sites only receive the result.',
  },
  loading: {
    title: 'Configurando un sistema de verificación seguro',
    description: 'Tu información personal nunca se recopila, almacena ni comparte',
    status: {
      initializing: 'Inicializando...',
      settingUpApiKey: 'Configurando clave API...',
      ready: 'Listo para comenzar la verificación',
      errorRetrying: 'Error al iniciar: reintentando...',
    },
    errors: {
      noApiKey: 'No hay clave API configurada. Proporciona una clave API.',
    },
  },
  cameraAccess: {
    title: 'Se requiere acceso a la cámara',
    description: 'Se necesita acceso a la cámara para verificar tu edad y continuar',
    continueDesktop: 'Continuar en el escritorio',
    separatorText: 'O',
    noWebcam: '¿No tienes webcam?',
    privacyNotice: 'Tu imagen se procesa al instante y se elimina de inmediato',
    qr: {
      title: 'Continuar en tu teléfono',
      description: 'Escanea este código para completar la verificación en el móvil',
      imageAlt: 'Código QR para verificación móvil',
      instruction: 'Apunta la cámara de tu teléfono a este código',
      waiting: 'Esperando conexión...',
      generating: 'Generando código QR...',
      errorMissingSession: 'Falta el ID de sesión. Vuelve atrás e inténtalo de nuevo.',
      errorUnavailable: 'No se pudo generar el código QR. Inténtalo de nuevo.',
      errorFailed: 'No se pudo generar el código QR. Inténtalo de nuevo.',
    },
  },
  cameraSetup: {
    defaultMessage: 'Coloca tu rostro dentro del círculo',
    warmup: {
      preparing: 'Preparando tu verificación...',
      preparingDelayed:
        'La conexión con el servicio de verificación está tardando más de lo esperado. Mantén esta página abierta.',
      requesting: 'Esperando permiso de cámara...',
      initializing: 'Iniciando la cámara...',
      positioning: 'Preparando la imagen de la cámara...',
      delayed:
        'Esto está tardando más de lo esperado. Mantén esta página abierta mientras se inicia la cámara.',
    },
    analysis: {
      title: 'Procesando tu foto',
      description: 'Esto suele tardar unos segundos.',
      privacy: 'Las imágenes se procesan de forma segura y no se almacenan.',
      failureMessage: 'No pudimos completar la verificación. Inténtalo de nuevo.',
    },
    errors: {
      stalledTitle: 'La cámara no se inició',
      interruptedTitle: 'La cámara se interrumpió',
      missingSessionId: 'Falta el ID de sesión en el resultado de verificación.',
      faceTimeout:
        'Tiempo de espera agotado en la detección del rostro. Asegúrate de que tu rostro sea visible en la cámara.',
      faceTimeoutDetail:
        'No se detectó ningún rostro en el tiempo requerido. Inténtalo de nuevo y asegúrate de que tu rostro esté claramente visible en el círculo.',
    },
  },
  cameraFeedback: {
    initial_position: 'Centra tu rostro en el marco',
    initial_lighting: 'Busca buena iluminación',
    initial_stability: 'Mantén la cámara estable',
    wide_zone_position: 'Centra tu rostro en el marco',
    wide_zone_center: 'Centra tu rostro en el marco',
    wide_too_close: 'Aléjate un poco',
    wide_off_center: 'Centra tu rostro en el marco',
    wide_movement: 'Mantente quieto',
    wide_gaze: 'Mira al frente',
    wide_success: '¡Buena posición!',
    zone_transition: '¡Perfecto! Ahora acércate un poco',
    great_now_move_closer: '¡Perfecto! Ahora acércate un poco',
    close_zone_position: '¡Perfecto! Quédate ahí',
    close_zone_gaze: 'Mira al frente',
    close_too_far: 'Acércate un poco',
    close_too_close: 'Aléjate un poco',
    close_off_center: 'Centra tu rostro en el marco',
    close_movement: 'Mantente quieto',
    close_gaze: 'Mira al frente',
    close_success: '¡Perfecto! Quédate ahí',
    processing: '¡Listo! Procesando...',
    no_face_detected: 'No se detecta rostro',
    move_closer: 'Acércate un poco',
    move_further: 'Aléjate un poco',
    center_face: 'Centra tu rostro en el marco',
    look_at_camera: 'Mira a la cámara',
    improve_lighting: 'Intentémoslo de nuevo',
    good_position: '¡Se ve bien!',
    move_closer_to_camera: 'Acércate un poco',
    move_further_from_camera: 'Aléjate un poco',
    good_wide_position: '¡Buena posición!',
    good_close_position: '¡Perfecto! Quédate ahí',
  },
  cameraProcessing: {
    verifying: '¡Buen trabajo! Procesando tu verificación',
    analyzing: 'Analizando...',
  },
  idRequest: {
    l2: {
      title: 'Se requiere verificación con ID',
      description: 'Proporciona un ID emitido por el gobierno para completar la verificación',
    },
    l1: {
      failed: {
        title: 'Nuestro escaneo no pudo confirmar tu edad',
        description: 'Necesitamos una verificación rápida con ID para continuar',
      },
      unavailable: {
        title: 'Terminemos con una verificación rápida de ID',
        description:
          'Esta vez no pudimos comprobar tu edad con tu selfie, así que usaremos tu ID.',
      },
      additional: {
        title: 'Se necesita verificación adicional',
        description: 'Para cumplir con la normativa, debemos verificar tu edad con un ID',
      },
    },
    primaryAction: 'Continuar',
    privacyNotice: 'No almacenamos ni compartimos tu información personal',
    privacyTooltip:
      'Tu ID solo se usa para verificar tu edad. Nunca se guarda, almacena ni comparte con el sitio web solicitante. El sitio web solicitante solo recibe una simple confirmación de que cumples con el requisito de edad.',
    iconAlt: 'Tarjeta de identificación',
  },
  idCapture: {
    errorTitle: 'Falló la captura de la foto',
    initializingTitle: 'Inicializando cámara',
    initializingDescription: 'Preparando la captura del documento...',
    title: 'Toma una foto del frente de tu ID',
    recaptureTitle: 'No pudimos aceptar esa foto de tu ID',
    instructions:
      'Asegúrate de que tu ID esté completamente visible, con texto claro y todos los bordes dentro del marco',
    recaptureInstructions:
      'Asegúrate de que tu ID esté claro y completamente visible, y vuelve a tomar la foto.',
    capturing: 'Capturando...',
    recaptureAction: 'Volver a tomar la foto',
    captureAction: 'Escanear ID',
    capturedTitle: '¡Foto capturada!',
    capturedDescription: 'Preparando revisión...',
    cameraErrorTitle: 'Error de cámara',
    cameraErrorAlt: 'Error de cámara',
    cameraRecoveryDescription:
      'Intenta de nuevo, cambia de cámara si está disponible o verifica que este navegador tenga permiso para usar la cámara.',
    cameraPermissionHelp:
      'En iPhone o iPad, revisa el acceso a la cámara de Safari en Configuración. En Chrome o Safari, usa el ícono de cámara en la barra de direcciones para permitir el acceso.',
    switchCameraAction: 'Cambiar cámara',
  },
  idReview: {
    title: 'Revisa tu ID escaneado',
    description: 'Asegúrate de que toda la información sea claramente visible',
    imageAlt: 'ID capturado',
    guidance: 'Tu ID debe ser visible y legible.',
    missingImage: 'No se capturó ninguna imagen. Vuelve atrás y captura tu ID.',
    submitAction: 'Continuar',
    retakeAction: 'Repetir',
  },
  idProcessing: {
    title: 'Verificando tu ID',
    description:
      'Este proceso suele tardar entre 20 y 30 segundos. En algunos casos puede tardar hasta 60 segundos.',
    keepOpen: 'Mantén esta página abierta mientras se completa la verificación.',
    info: 'Tu documento se procesa de forma segura. No se almacena información personal.',
    status: {
      initial: 'Verificación en curso.',
      stillWorking: 'Todavía estamos verificando tu documento...',
      almostDone: 'Ya casi está. Gracias por tu paciencia.',
    },
    steps: {
      received: 'Documento recibido',
      analyzing: 'Analizando información',
      verifying: 'Verificando autenticidad',
      confirming: 'Confirmando edad',
    },
    errors: {
      noDocumentData: 'No se encontraron datos del documento. Captura tu ID nuevamente.',
      noSessionId: 'No hay ID de sesión disponible. Reinicia la verificación.',
      selfieFetchFailed:
        'No se pudo recuperar el selfie del servidor. Reinicia la verificación.',
      noApiKey: 'La clave API no está disponible. Actualiza e inicia de nuevo.',
      generic: 'La verificación falló.',
    },
  },
  desktopWaiting: {
    title: 'Dispositivo conectado',
    description: 'Completa la verificación en tu dispositivo móvil',
    iconAlt: 'Dispositivo móvil',
    keepOpen: 'Mantén esta ventana abierta',
    redirectInfoLine1: 'Serás redirigido automáticamente cuando',
    redirectInfoLine2: 'la verificación se complete.',
    expiredTitle: 'Sesión expirada',
    expiredDescription: 'Tu sesión de verificación ha expirado. Comienza de nuevo.',
  },
  returnToDesktop: {
    title: 'Vuelve a tu escritorio',
    description:
      'Verificación móvil completa. Regresa a tu navegador de escritorio para continuar.',
    iconAlt: 'Computadora de escritorio',
  },
  recovery: {
    title: '¿Continuar verificación?',
    description: 'Encontramos una sesión de verificación en curso',
    lastSeen: 'Última vez: {{screen}}',
    progressLine: '{{age}} • {{progress}}',
    progress: {
      start: 'Recién comenzando',
      early: 'Al inicio del proceso',
      good: 'Buen progreso',
      halfway: 'Más de la mitad',
      almost: 'Casi terminado',
      nearlyComplete: 'Casi completo',
    },
    time: {
      minuteAgo: 'hace {{count}} minuto',
      minutesAgo: 'hace {{count}} minutos',
      secondAgo: 'hace {{count}} segundo',
      secondsAgo: 'hace {{count}} segundos',
    },
    screens: {
      main: 'Bienvenido',
      emblemStepUp: 'Emblem Step Up',
      cameraSetup: 'Configuración de cámara',
      verification: 'Verificación de edad',
      idRequest: 'ID requerido',
      idCapture: 'Captura de documento',
      idProcessing: 'Procesando documento',
      idReview: 'Revisión de captura',
      success: 'Verificación completa',
      failure: 'Verificación fallida',
      error: 'Error técnico',
      unknown: '{{screenName}}',
    },
    actions: {
      continue: 'Continuar donde lo dejé',
      startFresh: 'Comenzar de nuevo',
    },
    capabilities: {
      connection: '✓ Se puede restaurar la conexión',
      camera: '✓ Configuración de cámara guardada',
      manual: 'Algunas configuraciones pueden necesitar reconfiguración',
    },
    info: {
      continuingLabel: 'Continuando:',
      continuingDescription: 'Reanuda la verificación desde donde la dejaste.',
      startFreshLabel: 'Comenzar de nuevo:',
      startFreshDescription: 'Inicia una nueva sesión de verificación.',
    },
  },
  errorScreen: {
    helpTitle: 'Qué puedes hacer:',
    invalidSession: {
      title: 'Sesión inválida',
      description:
        'La sesión de verificación es inválida o ha expirado. Inicia el proceso nuevamente.',
      primaryAction: 'Contactar soporte',
      suggestion1: 'Vuelve al sitio del comercio',
      suggestion2: 'Inicia el proceso de verificación otra vez',
      suggestion3: 'Contacta soporte si el problema persiste',
    },
    expiredSession: {
      title: 'Sesión expirada',
      description:
        'Tu sesión de verificación expiró. Regresa al sitio o la aplicación donde comenzaste.',
      primaryAction: 'Volver al sitio',
      suggestion1: 'Regresa al sitio o la aplicación donde comenzaste',
      suggestion2: 'Inicia allí una nueva verificación',
      suggestion3: 'Contacta con el sitio si necesitas ayuda',
    },
    network: {
      title: 'Problema de conexión',
      description:
        'No se pudo conectar con los servicios de verificación. Revisa tu conexión e inténtalo de nuevo.',
      suggestion1: 'Revisa tu conexión a internet',
      suggestion2: 'Desactiva la VPN si la estás usando',
      suggestion3: 'Intenta recargar la página',
      suggestion4: 'Contacta soporte si el problema continúa',
    },
    unsupported: {
      title: 'Navegador no compatible',
      description:
        'Tu navegador no soporta las funciones necesarias para la verificación de edad. Usa un navegador moderno como Chrome, Firefox o Safari.',
      primaryAction: 'Descargar Chrome',
      suggestion1: 'Actualiza tu navegador',
      suggestion2: 'Prueba con Chrome, Firefox o Safari',
      suggestion3: 'Activa JavaScript si está deshabilitado',
    },
    cameraDenied: {
      title: 'Se requiere acceso a la cámara',
      description:
        'La verificación de edad requiere acceso a la cámara. Permite el acceso e inténtalo de nuevo.',
      suggestion1: 'Haz clic en el ícono de cámara en la barra de direcciones',
      suggestion2: 'Selecciona "Permitir" para el acceso a la cámara',
      suggestion3: 'Recarga la página después de otorgar permiso',
    },
    missingReturnUrl: {
      title: 'Error de configuración',
      description:
        'La sesión de verificación no tiene la configuración requerida. Contacta al comercio para corregir su integración.',
      primaryAction: 'Contactar al comercio',
      suggestion1: 'Vuelve al sitio del comercio',
      suggestion2: 'Contacta al equipo de soporte del comercio',
      suggestion3: 'Informa que su integración de {{productName}} debe actualizarse',
      suggestion4: 'El comercio debe proporcionar una URL de retorno válida en la configuración del SDK',
    },
    directAccess: {
      title: 'Acceso directo no permitido',
      description:
        'La verificación de edad debe iniciarse desde el sitio de un comercio usando el SDK de {{productName}}. No puedes acceder a este servicio directamente.',
      primaryAction: 'Cerrar página',
      iconAlt: 'Acceso bloqueado',
      suggestion1: 'Regresa al sitio del comercio donde comenzaste',
      suggestion2: 'Completa la verificación de edad en el proceso de compra o registro',
      suggestion3: 'No guardes ni compartas esta URL de verificación',
      suggestion4: 'Cada sesión de verificación es única y debe iniciarse por el comercio',
    },
    subscriptionRequired: {
      title: 'Se requiere suscripción',
      description:
        'Esta cuenta necesita una suscripción activa para verificaciones en producción. Contacta a tu administrador para actualizar el plan.',
      primaryAction: 'Contactar al administrador',
      portalAction: 'Abrir portal de facturación',
    },
    planLimitReached: {
      title: 'Límite mensual alcanzado',
      description:
        'Esta cuenta alcanzó su límite mensual de verificaciones. Contacta a tu administrador para actualizar el plan o espera al próximo período de facturación.',
      primaryAction: 'Contactar al administrador',
      portalAction: 'Abrir portal de facturación',
    },
    sandboxLimitReached: {
      title: 'Límite de sandbox alcanzado',
      description:
        'Esta cuenta alcanzó su límite de verificaciones en sandbox. Pide a un administrador que desactive el modo sandbox o actualice el plan.',
      primaryAction: 'Contactar al administrador',
      portalAction: 'Abrir portal de facturación',
    },
  },
  failure: {
    genericError: 'La verificación falló',
    iconAlt: 'Verificación fallida',
    document: {
      title: 'Verificación fallida',
      description: 'No pudimos completar tu verificación. Intentémoslo de nuevo.',
      reason1: 'Problemas con la calidad de la imagen del documento',
      reason2: 'La información no es claramente visible',
      reason3: 'Tipo de documento no compatible',
      reason4: 'No se cumplieron los requisitos de verificación de edad',
    },
    face: {
      title: 'Verificación fallida',
      description: 'No pudimos completar tu verificación. Intentémoslo de nuevo.',
      reason1: 'La verificación de vida no pasó',
      reason2: 'Condiciones de iluminación deficientes',
      reason3: 'Problemas de posicionamiento de la cámara',
      reason4: 'Movimiento durante el escaneo',
      reason5: 'Rostro no claramente visible',
    },
    crossDeviceRestart: {
      title: 'Vuelve a tu escritorio',
      description:
        'Esta verificación debe reiniciarse desde el navegador de escritorio que mostró el código QR. Vuelve allí, haz clic en Intentar de nuevo y luego escanea el nuevo código QR con tu teléfono.',
    },
    errors: {
      restartFailed: 'No se pudo reiniciar la verificación. Actualiza la página e inténtalo de nuevo.',
      switchFailed: 'No se pudo cambiar a móvil. Actualiza la página e inténtalo de nuevo.',
    },
  },
  success: {
    title: 'Verificación de edad exitosa',
    emblemTitle: '¡Cuenta de Emblem creada!',
    emblemDescription:
      'Tu verificación de edad portátil está lista. Puedes usar tu cuenta de Emblem para verificar tu edad en cualquier sitio que admita Emblem.',
    iconAlt: 'Éxito',
    continueAction: 'Continuar al sitio',
    separatorText: 'omitir futuras verificaciones',
    createEmblemAction: 'Crear cuenta de Emblem',
    emblemEnrollmentComplete: 'Cuenta Emblem creada',
    readyDescription: 'Ahora puedes acceder a tu contenido',
    redirectingIn: 'Redirigiendo en {{seconds}} segundos...',
    returnUrlInvalid:
      'La URL de retorno no está configurada o es inválida. Haz clic en Continuar para seguir.',
    errors: {
      missingReturnUrl: 'Falta la URL de retorno: mala configuración del SDK del comercio',
      emblemSignupFailed: 'No se pudo crear la cuenta de Emblem',
    },
  },
  emblem: {
    info: {
      ariaLabel: 'Aprende más sobre Emblem',
      trigger: '¿Qué es Emblem?',
      title: '¿Qué es Emblem?',
      paragraph1:
        'Emblem guarda de forma segura tu verificación de edad para que puedas demostrar que eres mayor de 18 sin compartir datos personales.',
      paragraph2:
        'Después de verificar tu edad con {{productName}}, puedes crear una cuenta de Emblem para omitir futuras verificaciones en sitios participantes. Tu verificación permanece privada; los sitios solo saben que estás verificado.',
      linkLabel: 'Sitio de Emblem',
    },
    signupInfo: {
      ariaLabel: 'Aprende más sobre crear una cuenta de Emblem',
      trigger: '¿Por qué crear una cuenta?',
      title: 'Crear cuenta de Emblem',
      intro: '¡Guarda tu verificación para el futuro! Con una cuenta de Emblem puedes:',
      bullet1: 'Omitir futuras verificaciones de edad en sitios participantes',
      bullet2: 'Mantener tu información personal privada',
      bullet3: 'Acceder al contenido restringido al instante',
      outro:
        'Tu verificación se almacena de forma segura. Los sitios solo saben que estás verificado.',
      linkLabel: 'Sitio de Emblem',
    },
  },
  mobileCompletion: {
    title: 'Verificación completa',
    description:
      'La verificación móvil se completó correctamente. Puedes cerrar esta ventana y volver a tu escritorio.',
    iconAlt: 'Computadora de escritorio',
    closeWindowFallback:
      'Cierra esta pestaña/ventana manualmente para volver a tu escritorio.',
  },
  cameraErrorBoundary: {
    iconAlt: 'Error de cámara',
    trySteps: 'Prueba estos pasos:',
    skipCamera: 'Continuar sin cámara',
    permission: {
      title: 'Se requiere acceso a la cámara',
      description: 'Para verificar tu edad necesitamos acceso a la cámara.',
      step1: 'Haz clic en el ícono de cámara en la barra de direcciones',
      step2: 'Selecciona "Permitir" para el acceso a la cámara',
      step3: 'Haz clic en "Intentar de nuevo" abajo',
    },
    notFound: {
      title: 'No se detectó cámara',
      description: 'No encontramos una cámara en tu dispositivo.',
      step1: 'Verifica que la cámara esté conectada correctamente',
      step2: 'Prueba con otro navegador',
      step3: 'Usa un dispositivo con cámara incorporada',
    },
    inUse: {
      title: 'Cámara en uso',
      description: 'Tu cámara está siendo usada por otra aplicación.',
      step1: 'Cierra otras apps que puedan usar la cámara',
      step2: 'Revisa videollamadas o software de grabación',
      step3: 'Reinicia el navegador e inténtalo de nuevo',
    },
    unsupported: {
      title: 'Navegador no compatible',
      description: 'Tu navegador no admite acceso a la cámara.',
      step1: 'Actualiza tu navegador a la última versión',
      step2: 'Prueba con Chrome, Firefox, Safari o Edge',
      step3: 'Habilita WebRTC en la configuración del navegador',
    },
    default: {
      title: 'Error de cámara',
      description: 'Hubo un problema al acceder a tu cámara.',
      step1: 'Revisa la conexión de la cámara',
      step2: 'Recarga la página e inténtalo de nuevo',
      step3: 'Prueba con otro navegador',
    },
    platform: {
      chrome: 'Chrome/Edge:',
      chromeInstruction: 'Haz clic en el ícono de cámara en la barra de direcciones',
      firefox: 'Firefox:',
      firefoxInstruction: 'Haz clic en el ícono de permisos junto a la URL',
      safari: 'Safari:',
      safariInstruction: 'Ve a Safari -> Configuración -> Sitios web -> Cámara',
    },
  },
  verificationErrorBoundary: {
    iconAlt: 'Error',
    retryAttempt: 'Intento {{attempt}} de {{max}}',
    errorDetails: 'Detalles del error',
    errorType: 'Tipo de error:',
    errorTypeGeneral: 'general',
    componentStack: 'Pila de componentes:',
    cameraDenied: {
      title: 'Acceso a la cámara denegado',
      description:
        'Permite el acceso a la cámara en la configuración del navegador para continuar.',
    },
    noCamera: {
      title: 'No se encontró cámara',
      description: 'Asegúrate de tener una cámara funcional conectada al dispositivo.',
    },
    cameraError: {
      title: 'Error de cámara',
      description:
        'Se produjo un problema con la cámara. Revisa la cámara e inténtalo de nuevo.',
    },
    documentFailed: {
      title: 'Falló la captura del documento',
      description:
        'Tuvimos problemas al capturar tu documento. Asegura buena iluminación e inténtalo de nuevo.',
    },
    connectionLost: {
      title: 'Conexión perdida',
      description:
        'Se perdió la conexión con nuestros servidores. Revisa tu conexión e inténtalo de nuevo.',
    },
    multipleErrors: {
      title: 'Se produjeron varios errores',
      description:
        'Estamos teniendo problemas persistentes. Inténtalo más tarde o contacta soporte.',
    },
    default: {
      title: 'Algo salió mal',
      description:
        'Se produjo un error inesperado. Inténtalo de nuevo o reinicia si el problema persiste.',
    },
  },
  errorState: {
    title: 'Algo salió mal',
  },
  notifications: {
    errorTitle: 'Error',
    successTitle: 'Éxito',
    infoTitle: 'Info',
    cameraAccessDenied:
      'Se denegó el acceso a la cámara. Concede permisos e inténtalo de nuevo.',
    noCameraFound: 'No se encontró cámara. Asegúrate de que tu dispositivo tenga cámara.',
    cameraInUse: 'La cámara ya está en uso por otra aplicación.',
    networkError: 'Error de red. Revisa tu conexión e inténtalo de nuevo.',
    unexpectedError: 'Ocurrió un error inesperado. Inténtalo de nuevo.',
  },
  cameraErrors: {
    notAllowed:
      'Necesitamos acceso a la cámara para continuar. Selecciona "Intentar de nuevo" para solicitarlo. Si tu navegador no vuelve a preguntar, permite el acceso a la cámara para este sitio en la configuración del navegador y luego inténtalo de nuevo o recarga la página.',
    notAllowedAction: 'Haz clic en Permitir cuando se solicite el acceso a la cámara',
    security:
      'El acceso a la cámara está bloqueado por la configuración de seguridad del navegador o del dispositivo. Permite el acceso para este sitio en esa configuración y vuelve a intentarlo o recarga la página.',
    securityAction:
      'Permite el acceso a la cámara en la configuración de seguridad del navegador o del dispositivo',
    notFound: 'No se encontró cámara. Asegúrate de que haya una cámara conectada.',
    notFoundAction: 'Conecta un dispositivo de cámara',
    notReadable: 'La cámara ya está en uso por otra aplicación.',
    notReadableAction: 'Cierra otras aplicaciones que estén usando la cámara',
    overconstrained: 'La configuración de la cámara no es compatible. Intentando con valores predeterminados...',
    overconstrainedAction: 'Se reintentará con ajustes básicos de cámara',
    timeout: 'Tiempo de espera agotado al inicializar la cámara. Inténtalo de nuevo.',
    timeoutAction: 'Inténtalo de nuevo',
    videoStalled:
      'La cámara se encendió, pero no llegó ninguna imagen. Selecciona "Intentar de nuevo" para reiniciar la cámara. Si el problema continúa, cierra otras aplicaciones que usen la cámara o recarga la página.',
    videoStalledAction: 'Inténtalo de nuevo para reiniciar la cámara',
    interrupted:
      'La cámara se detuvo de forma inesperada, posiblemente por una llamada u otra aplicación. Selecciona "Intentar de nuevo" para reiniciar la cámara.',
    interruptedAction: 'Inténtalo de nuevo para reiniciar la cámara',
    initializationFailed: 'Falló la inicialización de la cámara. Recarga e inténtalo de nuevo.',
    streamUnavailable: 'El flujo de la cámara no está disponible. Revisa la conexión de la cámara.',
    generic: 'Falló el acceso a la cámara. Revisa la cámara e inténtalo de nuevo.',
    genericAction: 'Recarga la página e inténtalo de nuevo',
  },
  brandHeader: {
    tagline: 'Verificación de edad',
    merchantLabel: 'Verificando para',
    secureLabel: 'Seguro',
  },
  faceMatchRecovery: {
    selfieRetry: {
      title: 'Probemos con otra selfie',
      message:
        'Esta vez no pudimos relacionar tu selfie con la foto de tu ID. Las fotos de los documentos pueden tener años, y los cambios de apariencia o de iluminación pueden hacer que sea más difícil reconocerte.',
      tipsHeading: 'Prueba estos consejos para tu próxima selfie:',
      tips: {
        straightOn: 'Mira directamente a la cámara y mantén toda tu cara en el encuadre.',
        lighting: 'Busca un lugar bien iluminado, con la luz delante de ti.',
        expression:
          'Intenta poner la misma expresión que en la foto de tu ID. Si no sonríes en esa foto, mantén una expresión relajada y neutra.',
        eyes: 'Asegúrate de que tus ojos se vean con claridad, sin reflejos ni gafas de sol.',
      },
      primaryAction: 'Tomar una nueva selfie',
      captureHeading: 'Para tu nueva selfie',
      captureTips: {
        straightOn: 'Mira directamente a la cámara',
        lighting: 'Luz delante de ti',
        expression: 'La misma expresión que en tu ID',
        eyes: 'Ojos bien visibles, sin gafas de sol',
      },
      comparing: 'Comparando tu nueva selfie con tu ID…',
    },
    repeated: {
      title: '¿Sigues teniendo problemas? Volvamos a intentarlo.',
      message:
        'Una foto de ID antigua o una iluminación diferente pueden dificultarlo. Mira a la cámara con buena luz e intenta poner la misma expresión que en la foto de tu ID.',
      primaryAction: 'Tomar otra selfie',
    },
    help: {
      heading: '¿Necesitas ayuda?',
      differentIdText:
        'Si tienes otro documento con una foto más reciente tuya, puedes usarlo en su lugar.',
      differentIdAction: 'Usar otro documento',
    },
    differentId: {
      captureTitle: 'Toma una foto de tu otro documento',
      captureInstructions:
        'Toma una foto del frente del otro documento. Asegúrate de que esté claro y completamente visible.',
    },
    retainedIdUnavailable:
      'Necesitamos una nueva foto de tu ID para terminar. Asegúrate de que esté claro y completamente visible.',
  },
} as const satisfies TranslationShape;
